import { useEffect, useContext, useRef, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { useAuthArgs } from '../hooks/useAuthArgs';
import { createCdnWidgetTransport, type WidgetTransport } from '../api/cdnWidgetTransport';
import { cdnWidgetUrl } from '../config/cdnWidget';
import { RuntimeConfigContext } from '../config/runtimeConfigContext';
import { getUserInfo } from '../api/userClient';
import { getCustomerContext } from '../api/customerClient';
import type { AuthArgs } from '../api/authArgs';

const retainedTransports = new Map<string, {
  transport: WidgetTransport;
  getAuthArgs: () => Promise<AuthArgs | null>;
}>();

// The imperative surface the CDN widget bundle installs on window (see
// partner/widget/src/mount.ts). We only call `mount`; the returned handle lets
// us tear the widget down on unmount.
interface CmspWidgetHandle {
  update(patch: Record<string, unknown>): void;
  unmount(): void;
}
interface CmspWidgetGlobal {
  (command: 'mount', target: string | Element, config: unknown): CmspWidgetHandle | void;
  /** Present ONLY on the real bundle API (not the loader stub) — use as the ready signal. */
  version?: string;
  q?: unknown[];
}
declare global {
  // eslint-disable-next-line no-var
  interface Window {
    CmspWidget?: CmspWidgetGlobal;
  }
}

// Install the loader stub + inject the CDN <script type="module"> exactly once per document.
// The stub buffers any mount() calls made before the bundle finishes loading;
// the bundle drains that queue on install (mount.ts installGlobal). The widget ships as an
// ES module (code-split: adaptivecards loads lazily as a separate chunk), so the tag MUST be
// type="module" for the dynamic import() chunks to resolve. Under a module script
// document.currentScript is null, so the bundle resolves its sibling widget.css via
// import.meta.url; data-cmsp-css is kept as a harmless explicit fallback pin.
let scriptInjected = false;
function ensureWidgetScript(url: string): void {
  const w = window as Window;
  if (!w.CmspWidget) {
    const stub = function (this: unknown, ...args: unknown[]) {
      (stub.q = stub.q || []).push(args);
    } as CmspWidgetGlobal & { q: unknown[] };
    stub.q = [];
    w.CmspWidget = stub;
  }
  if (scriptInjected) return;
  scriptInjected = true;
  const cssUrl = url.replace(/widget\.js(\?.*)?$/, 'widget.css');
  const script = document.createElement('script');
  script.type = 'module';
  script.src = url;
  script.async = true;
  script.setAttribute('data-cmsp-css', cssUrl);
  document.head.appendChild(script);
}

export interface CdnChatWidgetProps {
  /** Routing id forwarded to the widget + transport (from the agent selector). */
  entryPoint: string;
}

/**
 * Bring-up embedding of the CDN-hosted generic widget in the first-party portal.
 * Loads widget.js from the partner CDN and mounts it into a floating dock, driven
 * by a same-origin host transport wired to the SPA's MSAL identity. Rendered
 * ALONGSIDE the built-in <ChatWidget/> (in its own dock) when the CDN feature
 * flag is on, so the two can be compared side by side.
 */
export function CdnChatWidget({ entryPoint }: CdnChatWidgetProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const getAuthArgs = useAuthArgs();
  const { account } = useAuth();
  const cfg = useContext(RuntimeConfigContext);
  // Keep the latest token resolver + caller app id reachable from the (possibly
  // deferred) mount without re-mounting the widget on every auth/config change.
  const getAuthArgsRef = useRef(getAuthArgs);
  getAuthArgsRef.current = getAuthArgs;
  const callerAppIdRef = useRef(cfg?.clientId);
  callerAppIdRef.current = cfg?.clientId;
  const transportRef = useRef<WidgetTransport | null>(null);
  const handleRef = useRef<CmspWidgetHandle | null>(null);
  const contextRevisionRef = useRef(0);
  const previousContextAccountRef = useRef(account?.homeAccountId);
  const [ownerRevision, setOwnerRevision] = useState(0);
  const contextLoadersRef = useRef({
    loadUserInfo: async () => {
      const auth = await getAuthArgsRef.current();
      if (!auth) throw new Error('Sign-in required');
      return getUserInfo(auth);
    },
    loadSubscriptions: async () => {
      const auth = await getAuthArgsRef.current();
      if (!auth) throw new Error('Sign-in required');
      return (await getCustomerContext(auth)).subscriptions;
    },
    accountUrl: 'https://account.microsoft.com',
    subscriptionsUrl: 'https://account.microsoft.com/services',
    devicesUrl: 'https://account.microsoft.com/devices',
  });

  // Mirrors the built-in assistant's header subtitle.
  const subtitle = account
    ? account.name
      ? `Signed in as ${account.name}`
      : 'Signed in for account help'
    : '';
  const subtitleRef = useRef(subtitle);
  subtitleRef.current = subtitle;
  // MSAL restores the account asynchronously, so a mount deferred until `cmsp:ready`
  // must read the current value rather than the first render's closure.
  const signedInRef = useRef(false);
  signedInRef.current = Boolean(account);

  useEffect(() => {
    ensureWidgetScript(cdnWidgetUrl);
    const target = containerRef.current;
    if (!target) return;

    let handle: CmspWidgetHandle | undefined;
    let cancelled = false;

    // Mount only once the REAL bundle API is installed (identified by `version`,
    // which the loader stub lacks). Mounting through the stub would buffer the
    // call and return void, so React's cleanup could not unmount it — leaving an
    // orphaned widget after unmount / entryPoint change.
    const doMount = () => {
      const api = window.CmspWidget;
      if (cancelled || !api || !api.version) return;

      let retained = retainedTransports.get(entryPoint);
      if (!retained) {
        const owner = { getAuthArgs: () => getAuthArgsRef.current() };
        const transport = createCdnWidgetTransport({
          entryPoint,
          callerAppId: callerAppIdRef.current,
          getAuthArgs: () => owner.getAuthArgs(),
        });
        retained = Object.assign(owner, { transport });
        retainedTransports.set(entryPoint, retained);
      }
      retained.getAuthArgs = () => getAuthArgsRef.current();
      const transport = retained.transport;
      transportRef.current = transport;

      const result = api('mount', target, {
        entryPoint,
        transport,
        presentation: 'floating',
        title: 'Support Assistant',
        subtitle: subtitleRef.current,
        badge: 'Widget',
        launcherText: 'Chat with AI',
        dockSide: 'left',
        welcomeTitle: "Hi, I'm your Microsoft support assistant.",
        privacyUrl: 'https://privacy.microsoft.com/privacystatement',
        contextPanels: {
          isSignedIn: signedInRef.current,
          revision: contextRevisionRef.current,
          ...contextLoadersRef.current,
        },
        theme: { primary: '#0078d4' },
        onEvent: (evt: { type: string; data?: unknown }) => {
          // Bring-up visibility: surface host-reaction events (handoff_requested,
          // sign_in_prompted, message_feedback, errors) so we can verify the
          // contract during the trial. Host wiring for these comes later.
          // eslint-disable-next-line no-console
          console.debug('[cdn-widget]', evt.type, evt.data ?? '');
        },
      });
      if (result) handle = result;
      handleRef.current = result ?? null;
    };

    if (window.CmspWidget?.version) {
      doMount();
    } else {
      document.addEventListener('cmsp:ready', doMount, { once: true });
    }

    return () => {
      cancelled = true;
      document.removeEventListener('cmsp:ready', doMount);
      handle?.unmount?.();
      transportRef.current?.detach();
      handleRef.current = null;
      transportRef.current = null;
    };
  }, [entryPoint, ownerRevision]);

  // Mid-conversation sign-in (or account switch): refresh the server-side identity cache in place,
  // mirroring the built-in ChatWidget. Re-mounting the widget would reset its transcript, so the
  // refresh goes through the transport and leaves the mounted widget untouched.
  useEffect(() => {
    if (previousContextAccountRef.current !== account?.homeAccountId) {
      previousContextAccountRef.current = account?.homeAccountId;
      contextRevisionRef.current += 1;
      if (transportRef.current?.hasBrowserSession()) {
        void transportRef.current.dispose().catch(error => console.warn('Live ownership teardown failed', error));
        setOwnerRevision(value => value + 1);
      }
    }
    void transportRef.current?.refreshIdentity().catch(error => console.warn('Live identity refresh failed', error));
    handleRef.current?.update({
      subtitle,
      contextPanels: {
        isSignedIn: Boolean(account),
        revision: contextRevisionRef.current,
        ...contextLoadersRef.current,
      },
    });
  }, [account?.homeAccountId, subtitle]);

  // Floating mode: the widget core renders its own fixed launcher + docked
  // window, so we only provide a bare (zero-layout) mount point. No transform
  // ancestor here, so the widget's position:fixed stays viewport-relative.
  return <div ref={containerRef} />;
}
